/**
 * Serializes an input object into a query string for GET/DELETE.
 *
 * **Empty means absent.** `undefined`, `null`, an empty array and an empty string
 * are all omitted rather than serialized as `key=`. The same rule applies inside an
 * array: empty entries are dropped before joining, so a list that holds nothing but
 * blanks omits its key instead of sending `key=%2C`. Callers therefore do not need
 * `if (value)` guards, and an SDK never needs a blank-dropping mapper — passing a
 * value through is enough.
 *
 * `key=` is a materially different request from an absent key: Spring binds
 * `@RequestParam(required = false) String` to `""` rather than `null`, and services
 * routinely use that value unguarded (growth's `getRedemption` feeds `planCode`
 * straight into a coupon cache key).
 */
const isEmpty = (value: unknown) => value === undefined || value === null || value === '';

export function stringify(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (isEmpty(value)) continue;

    if (Array.isArray(value)) {
      const entries = value.filter((entry) => !isEmpty(entry));

      if (entries.length > 0) {
        searchParams.set(key, entries.join(','));
      }
    } else {
      searchParams.set(key, String(value));
    }
  }

  return searchParams.toString();
}
